import React, { useState, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navbar, Nav, Container, Button, Offcanvas, Dropdown } from 'react-bootstrap';
import { HiSun } from 'react-icons/hi';
import { HiMoon } from 'react-icons/hi2';
import { FaSeedling, FaQuestionCircle, FaUserEdit, FaSignOutAlt } from 'react-icons/fa';
import { AuthContext } from '../contexts/AuthContext';
import { LanguageContext } from '../contexts/LanguageProvider';
import NavBar from './NavBar';
import LangSwitch from '../components/common/LangSwitch';
import LogoutButton from '../components/LogoutButton';
import NotificationDropdown from '../components/Notification/NotificationDropdown';
import WelcomeTutorial from '../components/Tutorial/WelcomeTutorial';
import '../styles/Header.css';

const translations = {
  pt: {
    login: 'Entrar',
    register: 'Registrar',
    sair: 'Sair',
    dark: 'Modo escuro',
    light: 'Modo claro',
    menu: 'Menu',
    help: 'Tutorial / Ajuda',
    appTitle: 'Bioinsumos na Cafeicultura',
    editProfile: 'Editar perfil',
    account: 'Conta',
    roles: { ADMIN: 'Administrador', EDITOR: 'Editor', READER: 'Leitor' },
  },
  en: {
    login: 'Sign in',
    register: 'Register',
    sair: 'Logout',
    dark: 'Dark mode',
    light: 'Light mode',
    menu: 'Menu',
    help: 'Tutorial / Help',
    appTitle: 'Bioinputs in Coffee Growing',
    editProfile: 'Edit profile',
    account: 'Account',
    roles: { ADMIN: 'Administrator', EDITOR: 'Editor', READER: 'Reader' },
  },
};

const initials = (value) => {
  const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const LogoutMenuItem = React.forwardRef((props, ref) => {
  const { setIsLoggedIn, setUsername, setRole } = useContext(AuthContext);
  const { language } = useContext(LanguageContext);
  const t = translations[language] || translations.pt;

  const handleLogout = () => {
    localStorage.removeItem('auth');
    localStorage.removeItem('role');
    localStorage.removeItem('name');
    localStorage.removeItem('username');
    setIsLoggedIn(false);
    setUsername('');
    setRole('');
    window.location.assign('/');
  };

  return (
    <button {...props} ref={ref} type="button" onClick={handleLogout}>
      <FaSignOutAlt className="me-2" /> {t.sair}
    </button>
  );
});

const Header = ({ themeNow, switchTheme }) => {
  const { isLoggedIn, username, name, role, setLoginModalOpen } = useContext(AuthContext);
  const { language, setPortuguese, setEnglish } = useContext(LanguageContext);

  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [logoOk, setLogoOk] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);

  const t = translations[language] || translations.pt;
  const isDark = themeNow === 'dark';
  const roleLabel = (t.roles && t.roles[role]) || role || '';
  const profilePath = role ? `/${role.toLowerCase()}/profile` : '/';

  const handleSelectLanguage = (lang) => {
    if (lang === 'pt') setPortuguese();
    else if (lang === 'en') setEnglish();
  };

  const toggleTheme = () => switchTheme(isDark ? 'light' : 'dark');
  const closeMobileMenu = () => setShowMobileMenu(false);

  useEffect(() => {
    if (isLoggedIn && username && !localStorage.getItem(`welcomeTutorialSeen_${username}`)) {
      setShowTutorial(true);
    }
  }, [isLoggedIn, username]);

  const closeTutorial = () => {
    if (username) localStorage.setItem(`welcomeTutorialSeen_${username}`, '1');
    setShowTutorial(false);
  };

  return (
    <>
      <Navbar fixed="top" expand="lg">
        <Container fluid className="px-3 px-xl-4">
          {isLoggedIn && (
            <div className="d-lg-none me-2">
              <NotificationDropdown align="start" width="300px" />
            </div>
          )}

          <Navbar.Brand as={Link} to="/" className="d-flex align-items-center brand-lockup me-0">
            {logoOk ? (
              <img
                src={process.env.PUBLIC_URL + '/biotrop-icon-white.png'}
                alt="Biotrop"
                className="brand-mark"
                onError={() => setLogoOk(false)}
              />
            ) : (
              <FaSeedling className="brand-icon" size={24} />
            )}
            <span className="brand-text d-none d-sm-flex">
              <span className="brand-name">BIOTROP</span>
              <span className="brand-tagline">Soluções em Tecnologia Biológica</span>
            </span>
          </Navbar.Brand>

          <span className="brand-divider d-none d-xxl-block" />
          <span className="brand-app-name d-none d-xxl-block">{t.appTitle}</span>

          <div className="d-none d-lg-flex ms-3">
            <NavBar />
          </div>

          <Navbar.Toggle aria-controls="app-mobile-menu" onClick={() => setShowMobileMenu(true)} />

          <Nav className="ms-auto d-none d-lg-flex align-items-center gap-1">
            {isLoggedIn && <NotificationDropdown />}
            {isLoggedIn && (
              <Button
                variant="none"
                className="btn-icon"
                onClick={() => setShowTutorial(true)}
                title={t.help}
                aria-label={t.help}
              >
                <FaQuestionCircle size={18} />
              </Button>
            )}
            <Button
              variant="none"
              className="btn-icon"
              onClick={toggleTheme}
              title={isDark ? t.light : t.dark}
              aria-label={isDark ? t.light : t.dark}
            >
              {isDark ? <HiSun size={20} /> : <HiMoon size={20} />}
            </Button>
            <span className="nav-divider" />
            <LangSwitch language={language} onSelect={handleSelectLanguage} />
            <span className="nav-divider" />

            {isLoggedIn ? (
              <Dropdown align="end">
                <Dropdown.Toggle as="button" type="button" className="user-chip">
                  <span className={`user-avatar role-${String(role || '').toLowerCase()}`}>
                    {initials(name || username)}
                  </span>
                  <span className="user-chip-text">
                    <span className="user-chip-name">{name || username}</span>
                    <span className="user-chip-role">{roleLabel}</span>
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Header>{t.account}</Dropdown.Header>
                  <Dropdown.Item as={Link} to={profilePath}>
                    <FaUserEdit className="me-2" /> {t.editProfile}
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item as={LogoutMenuItem} />
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              <>
                <Button variant="outline-light" size="sm" className="fw-bold" onClick={() => setLoginModalOpen(true)}>
                  {t.login}
                </Button>
                <Button as={Link} to="/register" size="sm" className="fw-bold btn-brand ms-1">
                  {t.register}
                </Button>
              </>
            )}
          </Nav>
        </Container>
      </Navbar>

      <Offcanvas
        id="app-mobile-menu"
        show={showMobileMenu}
        onHide={closeMobileMenu}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{t.menu}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0 d-flex flex-column">
          {isLoggedIn && (
            <div className="d-flex align-items-center gap-2 px-3 py-3 border-bottom">
              <span className={`user-avatar role-${String(role || '').toLowerCase()}`}>
                {initials(name || username)}
              </span>
              <div className="overflow-hidden">
                <div className="fw-bold text-truncate">{name || username}</div>
                <span className="role-badge">{roleLabel}</span>
              </div>
            </div>
          )}

          <div className="flex-grow-1 overflow-auto py-2">
            <NavBar isMobile onLinkClick={closeMobileMenu} />
          </div>

          <div className="border-top p-3 d-flex flex-column gap-3">
            <div className="d-flex align-items-center justify-content-between">
              <LangSwitch language={language} onSelect={handleSelectLanguage} />
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={toggleTheme}
                className="d-inline-flex align-items-center gap-2"
              >
                {isDark ? <HiSun size={16} /> : <HiMoon size={16} />}
                {isDark ? t.light : t.dark}
              </Button>
            </div>
            {isLoggedIn ? (
              <div className="d-grid gap-2">
                <Button as={Link} to={profilePath} variant="outline-secondary" onClick={closeMobileMenu}>
                  <FaUserEdit className="me-2" /> {t.editProfile}
                </Button>
                <LogoutButton text={t.sair} variant="outline-danger" />
              </div>
            ) : (
              <div className="d-grid gap-2">
                <Button className="btn-brand" onClick={() => { closeMobileMenu(); setLoginModalOpen(true); }}>
                  {t.login}
                </Button>
                <Button as={Link} to="/register" variant="outline-secondary" onClick={closeMobileMenu}>
                  {t.register}
                </Button>
              </div>
            )}
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {isLoggedIn && (
        <WelcomeTutorial
          show={showTutorial}
          onClose={closeTutorial}
          role={role}
          language={language}
        />
      )}
    </>
  );
};

export default Header;
